import React, { useEffect } from 'react';
import { Route, Switch, Router, RouteComponentProps, Redirect } from 'react-router-dom';
import AuthRoute from 'components/AuthRoute';
import Home from 'views/Home';
import Login from 'views/Login';
import Register from 'views/Register';
import PasswordReset from 'views/PasswordReset';
import Spinner from 'components/Spinner';
import * as IntercomService from 'services/intercom';
import { Routes } from 'routing';
import AuthUserActions from 'views/AuthUserActions';
import history from 'services/history';
import * as Analytics from 'common/services/analytics';
import WebSessionCounter from 'common/frontend/helpers/SessionCounter';
import { signInServer } from 'common/api/authentication';
import ScrollToTop from 'common/components/routing/ScrollToTop';
import { api } from 'common/frontend/api';
import { getFromStorage, Storages, StorageKeys, removeFromStorage } from 'common/utils/frontUtils';
import { onAuthStateChanged } from 'common/frontend/firebase/auth';
import { useDispatch, useSelector } from 'react-redux';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as ViewActions from 'actions/ViewActions';

const environment = process.env.REACT_APP_ENV;

const AppRouter = () => {
	const dispatch = useDispatch();
	const isAuthLoading = useSelector(ViewSelectors.isAuthLoading);

	const storeRedirect = (storeId: string, locationId?: string) => {
		if (environment === 'local' && storeId) {
			window.location.replace(
				`http://localhost:7000/${storeId}${locationId ? `/${locationId}` : ''}`,
			);
			return null;
		} else if (environment === 'development' && storeId) {
			window.location.replace(
				`https://store.dev.rentle.shop/${storeId}${locationId ? `/${locationId}` : ''}`,
			);
			return null;
		} else if (environment === 'production' && storeId) {
			window.location.replace(
				`https://store.rentle.shop/${storeId}${locationId ? `/${locationId}` : ''}`,
			);
			return null;
		} else {
			return <Redirect to={Routes.ROOT} />;
		}
	};

	useEffect(() => {
		const reservationId = getFromStorage(Storages.SESSION, StorageKeys.RESERVATION_ID);
		if (reservationId) {
			api().reservations.doc(reservationId).delete();
			removeFromStorage(Storages.SESSION, StorageKeys.RESERVATION_ID);
		}
	}, []);

	useEffect(() => {
		// Check if user has logged in or not
		const unsubscribe = onAuthStateChanged((authUser) => {
			dispatch(
				ViewActions.setAuthState({
					isAuthenticated: !!authUser,
					loading: false,
				}),
			);
			if (authUser) {
				signInServer(authUser);
				const { email } = authUser;

				IntercomService.boot({ email: email ?? undefined });
				Analytics.setGaUserProperties({ user_id: authUser.uid }, { sendToAllTags: true });
				WebSessionCounter.addSessionListener(() => {
					Analytics.setHubspotProperties(
						{
							rentle_lastseen_timestamp: new Date().getTime(),
						},
						email ?? undefined,
					);
					Analytics.addSession(email ?? undefined);
				});
			} else {
				IntercomService.logout();
				WebSessionCounter.removeSessionListener();
			}
		});
		return () => {
			unsubscribe();
		};
	}, [dispatch]);

	return (
		<Router history={history}>
			<ScrollToTop />
			<Switch>
				<Route path={Routes.LOGIN} component={Login} />
				<Route path={Routes.REGISTER} component={Register} />
				<Route path={Routes.PASSWORD_RESET} component={PasswordReset} />
				{/** This is hardcoded as it doesn't follow the naming convention for routes (would cause tests to fail)
				 * and is unlikely to be changed anyway
				 */}
				<Route path="/authAction" component={AuthUserActions} />
				{/* AUTH ROUTE CHECKS WRONG: CHECKS FOR AUTH NOT USER */}
				{!isAuthLoading ? (
					<React.Fragment>
						<Route
							path={Routes.PAY}
							render={(routeProps: RouteComponentProps<{ payId: string }>) => {
								const { payId } = routeProps.match.params;
								if (environment === 'local' && payId) {
									window.location.replace(`http://localhost:3000/${payId}`);
									return null;
								} else if (environment === 'development' && payId) {
									window.location.replace(`https://link.dev.rentle.shop/${payId}`);
									return null;
								} else if (environment === 'production' && payId) {
									window.location.replace(`https://link.rentle.shop/${payId}`);
									return null;
								} else {
									return <Redirect to={Routes.ROOT} />;
								}
							}}
						/>
						<Route
							path={Routes.STORE_ID_LOCATION}
							render={(
								routeProps: RouteComponentProps<{ storeId: string; locationId: string }>,
							) => {
								const { storeId, locationId } = routeProps.match.params;
								return storeRedirect(storeId, locationId);
							}}
						/>
						<Route
							path={Routes.STORE_ID}
							render={(routeProps: RouteComponentProps<{ storeId: string }>) => {
								const { storeId } = routeProps.match.params;
								return storeRedirect(storeId);
							}}
						/>
						<AuthRoute path={Routes.ROOT} component={Home} />
					</React.Fragment>
				) : (
					<Route component={() => <Spinner in />} />
				)}
			</Switch>
		</Router>
	);
};

export default AppRouter;
